import { createTaskFromObject } from '@/domain/models/task';
import syncApi from '@/data/api/sync';
import usersApi from '@/data/api/users';
import { createWebLocalDatabase } from './dexieLocalDatabase';
import { createSynchronizedEntityReadModel } from './createSynchronizedEntityReadModel';
import { createWebLocalSyncCoordinator } from '@/domain/services/sync/webLocalSyncCoordinator';

const toTaskMap = (records) =>
  records.reduce((tasks, record) => {
    const task = createTaskFromObject(record);
    tasks[task.id] = task;
    return tasks;
  }, {});

/**
 * Creates the web task read model used by the migrated task-list flow.
 *
 * Exposes both a persisted task snapshot for local-first rendering and a
 * server-to-local synchronization operation for callers that need remote
 * revalidation first.
 *
 * @param {Object} [options] - Data-layer dependencies for tests or bootstrap.
 * @param {Object} [options.database] - LocalDatabase adapter.
 * @param {Object} [options.pullApi] - Sync pull transport adapter.
 * @param {Object} [options.identity] - Authenticated identity provider.
 * @returns {Object} Local task read model.
 */
export const createTaskLocalReadModel = ({
  database = createWebLocalDatabase(),
  pullApi = syncApi,
  identity = usersApi,
  syncCoordinator = createWebLocalSyncCoordinator({
    database,
    pullApi,
    identity
  })
} = {}) => {
  const readModel = createSynchronizedEntityReadModel({
    entityType: 'task',
    syncCoordinator,
    materialize: toTaskMap,
    buildChangeSet: ({ changes, records }) => ({
      changes,
      tasks: toTaskMap(records)
    })
  });

  return {
    ...readModel,
    readLocalTasks: readModel.readLocalEntities,
    readChangedTasks: readModel.readChangedEntities
  };
};
